import type { ResponseSchemaFor } from '@upright/common/zod-utils';
import { buildRequestAndFetch } from '../fetcher-core';
import { logResponseValidationError } from '../logging';
import { getJsonOrUndefined } from '../response';
import { ApiTarget, getErrorMessage } from '../types';
import { ErrorWithHttpStatus } from '../error-with-http-status';
import { maybeHandleCustomError } from '../util';
import { getResponseValidationError } from './validate-response';

/**
 * This is the main fetcher for our API. It throws errors instead of returning object with error,
 * which plays well with Tanstack Query.
 *
 */
// eslint-disable-next-line upright/too-many-arguments
export async function fetchOrThrow<T>(
  path: string,
  method = 'GET',
  bodyContent?: any,
  apiTarget: ApiTarget = ApiTarget.SERVER,
  customHeaders: HeadersInit = {},
): Promise<T> {
  const response = await buildRequestAndFetch(path, method, bodyContent, apiTarget, customHeaders);
  if (response.ok) {
    const data = await response.text(); // Use .text() to catch non-json content for logging purposes
    try {
      const json: unknown = JSON.parse(data);
      return json as T;
    } catch (e) {
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('text/html')) {
        // CloudFront errorResponses transform 403/404 into 200+HTML, masking real API errors.
        // This is a stopgap measure to provide some kind of a fix.
        // See PLT-3624, 6963 and 6964. The real status code is only visible in ALB logs.
        throw new ErrorWithHttpStatus('Request failed: the server rejected the request', 410);
      }
      console.error('Response from service is not valid json', data);
      throw e;
    }
  } else {
    const errorData = await getJsonOrUndefined(response);
    await maybeHandleCustomError(errorData);
    const errorMessage = getErrorMessage(errorData);
    if (errorMessage) {
      throw new ErrorWithHttpStatus(errorMessage, response.status);
    } else {
      throw new ErrorWithHttpStatus(`${response.statusText} (${response.status})`, response.status);
    }
  }
}

/**
 * `fetchOrThrow` with the response body checked against a zod schema before the caller sees it.
 *
 * Most of this API layer trusts the server and casts (`fetchOrThrow<T>`), which is fine for the
 * majority of endpoints: a shape that drifts there makes a single component render wrong or
 * throw, and the type is the only documentation of the contract anyway. A few endpoints are not
 * like that — their payload feeds many surfaces at once, or its numbers are read through
 * defaulting accessors that turn a missing field into a plausible `0` or `false` rather than
 * into a crash. Those are worth a runtime check, and only those; validating everything would
 * mean 450 schemas to keep in step with the server for no added safety.
 *
 * To add an endpoint here:
 * - put the schema next to the response type in the matching `src/common/**\/api-types.ts`,
 *   named `z<ResponseType>`, and tie the two together with `responseSchemaFor`, which checks that
 *   the schema can only ever reject payloads that already violate the declared contract, and that
 *   the schema handed to this function is the one belonging to `T`;
 * - assert the fields the UI is load-bearing on, not the whole payload, and validate types
 *   rather than value domains — a schema that rejects a newly added enum member or a newly
 *   added field turns an additive backend change into an outage;
 * - unknown fields are neither rejected nor dropped: `z.object` ignores them, and the body is
 *   handed on as it arrived rather than as zod rebuilt it.
 */
export async function fetchAndValidateOrThrow<T>(params: {
  path: string;
  schema: ResponseSchemaFor<T>;
  method?: string;
  bodyContent?: unknown;
  apiTarget?: ApiTarget;
  customHeaders?: HeadersInit;
}): Promise<T> {
  const { path, schema, method = 'GET', bodyContent, apiTarget, customHeaders } = params;
  const body = await fetchOrThrow<unknown>(path, method, bodyContent, apiTarget, customHeaders);
  const validationError = getResponseValidationError({ endpoint: path, schema, body });
  if (validationError) {
    logResponseValidationError(validationError);
    throw validationError;
  }
  // The schema covers the load-bearing fields rather than all of `T`, so this is what turns
  // "checked" into "typed" — the same cast `fetchOrThrow` makes, with a check behind it.
  return body as T;
}

// Alternative version of above used when no data is returned. Returns the successful status code instead.
export async function fetchOrThrowNoReturnData(
  path: string,
  method = 'GET',
  bodyContent?: any,
  apiTarget: ApiTarget = ApiTarget.SERVER,
): Promise<number> {
  const response = await buildRequestAndFetch(path, method, bodyContent, apiTarget);
  if (response.ok) {
    // This is a stopgap measure to fix aforementioned cloudfront issue.
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('text/html')) {
      throw new ErrorWithHttpStatus('Request failed: the server rejected the request', 410);
    }
    return response.status;
  } else {
    const errorData = await getJsonOrUndefined(response);
    await maybeHandleCustomError(errorData);
    const errorMessage = getErrorMessage(errorData);
    if (errorMessage) {
      throw new ErrorWithHttpStatus(errorMessage, response.status);
    } else {
      throw new ErrorWithHttpStatus(`${response.statusText} (${response.status})`, response.status);
    }
  }
}

// eslint-disable-next-line upright/too-many-arguments
export async function fetchOrThrowBlob(
  path: string,
  method = 'GET',
  bodyContent?: any,
  apiTarget: ApiTarget = ApiTarget.SERVER,
  customHeaders: HeadersInit = {},
) {
  const response = await buildRequestAndFetch(path, method, bodyContent, apiTarget, customHeaders);
  if (response.ok) {
    // This is a stopgap measure to fix aforementioned cloudfront issue.
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('text/html')) {
      throw new ErrorWithHttpStatus('Request failed: the server rejected the request', 410);
    }
    const data = await response.blob();
    return data;
  } else {
    const errorData = await getJsonOrUndefined(response);
    await maybeHandleCustomError(errorData);
    const errorMessage = getErrorMessage(errorData);
    if (errorMessage) {
      throw new ErrorWithHttpStatus(errorMessage, response.status);
    } else {
      throw new ErrorWithHttpStatus(`${response.statusText} (${response.status})`, response.status);
    }
  }
}
